import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Wifi, WarningAmberOutlined } from '@mui/icons-material'
import FaceRecognitionIconMdi from 'mdi-react/FaceRecognitionIcon'
import CloseIconMdi from 'mdi-react/CloseIcon'

// Cast to avoid React types mismatch between mdi-react and project @types/react
const FaceRecognitionIcon = FaceRecognitionIconMdi as React.FC<{
  size?: number
  color?: string
}>
const CloseIcon = CloseIconMdi as React.FC<{ size?: number; color?: string }>

import { Button } from '../../index'
import * as S from './styles'

type NetworkStatus = 'excellent' | 'good' | 'poor' | 'critical'

interface NetworkQualityResult {
  status: NetworkStatus
  downlink: number
  /** From navigator.connection (browser-based quality) */
  effectiveType?: string
  rtt?: number
  saveData?: boolean
  /** From latency test (optional, for future use) */
  latency?: number
  jitter?: number
  isTesting?: boolean
}

const PING_ENDPOINTS = [
  'https://www.google.com/favicon.ico',
  'https://www.cloudflare.com/favicon.ico',
]

const LATENCY_THRESHOLDS = {
  excellent: 100,
  good: 200,
  poor: 400,
}

const JITTER_THRESHOLDS = {
  excellent: 20,
  good: 50,
  poor: 100,
}

async function measureLatency(url: string): Promise<number | null> {
  const start = performance.now()
  try {
    await fetch(`${url}?_=${Date.now()}`, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
    })
    return performance.now() - start
  } catch {
    return null
  }
}

async function runLatencyTest(rounds = 5): Promise<{
  latency: number
  jitter: number
  samples: number[]
} | null> {
  const results: number[] = []
  const endpoint =
    PING_ENDPOINTS[Math.floor(Math.random() * PING_ENDPOINTS.length)]

  await measureLatency(endpoint)

  for (let i = 0; i < rounds; i++) {
    const rtt = await measureLatency(endpoint)
    if (rtt !== null) {
      results.push(rtt)
    }
    if (i < rounds - 1) {
      await new Promise((r) => setTimeout(r, 200))
    }
  }

  if (results.length < 2) return null

  results.sort((a, b) => a - b)
  const trimmed = results.slice(1, -1)
  const samples = trimmed.length > 0 ? trimmed : results

  const avgLatency = samples.reduce((a, b) => a + b, 0) / samples.length

  let jitterSum = 0
  for (let i = 1; i < samples.length; i++) {
    jitterSum += Math.abs(samples[i] - samples[i - 1])
  }
  const jitter = samples.length > 1 ? jitterSum / (samples.length - 1) : 0

  return {
    latency: Math.round(avgLatency),
    jitter: Math.round(jitter),
    samples,
  }
}

function classifyNetwork(
  latency: number,
  jitter: number,
  browserDownlink: number
): NetworkStatus {
  if (
    latency <= LATENCY_THRESHOLDS.excellent &&
    jitter <= JITTER_THRESHOLDS.excellent
  ) {
    return browserDownlink > 0 && browserDownlink < 1.5 ? 'good' : 'excellent'
  }
  if (latency <= LATENCY_THRESHOLDS.good && jitter <= JITTER_THRESHOLDS.good) {
    return 'good'
  }
  if (latency <= LATENCY_THRESHOLDS.poor && jitter <= JITTER_THRESHOLDS.poor) {
    return 'poor'
  }
  return 'critical'
}

interface VideoPermissionsProps {
  onConfirm: () => void
  onClose: () => void
  setShowPermissions: (permissions: {
    camera: boolean
    microphone: boolean
  }) => void
  showPermissions: { camera: boolean; microphone: boolean }
}

const CAMERA_ACCESS_STATES = {
  granted: 'Permitida',
  denied: 'Negada',
  prompt: 'Aguardando permissão',  
  inacessible: 'Câmera indisponível',
}

const VideoPermissionsInner: React.FC<VideoPermissionsProps> = ({
  onConfirm,
  onClose,
  setShowPermissions,
  showPermissions,
}) => {
  const [cameraAccess, setCameraAccess] = useState<string | null>(null)
  const [microphoneAccess, setMicrophoneAccess] = useState<string | null>(null)
  const [isRequesting, setIsRequesting] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)
  const [networkQuality, setNetworkQuality] =
    useState<NetworkQualityResult | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const lastPermissionsRef = useRef<{ camera: boolean; microphone: boolean }>({
    camera: false,
    microphone: false,
  })
  const cameraAccessRef = useRef<string | null>(null)
  cameraAccessRef.current = cameraAccess

  const updateShowPermissionsIfChanged = useCallback(
    (camera: boolean, microphone: boolean) => {
      const next = { camera, microphone }
      if (
        lastPermissionsRef.current.camera === next.camera &&
        lastPermissionsRef.current.microphone === next.microphone
      ) {
        return
      }
      lastPermissionsRef.current = next
      setShowPermissions(next)
    },
    [setShowPermissions]
  )

  const checkSingleDevicePermission = async (
    device: 'camera' | 'microphone'
  ): Promise<'granted' | 'denied' | 'prompt' | 'inacessible'> => {
    try {
      const constraints =
        device === 'camera' ? { video: true } : { audio: true }
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      stream.getTracks().forEach((track) => track.stop())
      return 'granted'
    } catch (error: any) {
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        return 'denied'
      }
      if (
        device === 'camera' &&
        (error.name === 'NotReadableError' || error.name === 'NotFoundError')
      ) {
        return 'inacessible'
      }
      return 'prompt'
    }
  }

  const checkPermissionStatus = async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const cameraStatus = await navigator.permissions.query({
          name: 'camera' as PermissionName,
        })
        const microphoneStatus = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        })

        setCameraAccess(
          cameraStatus.state === 'granted'
            ? 'granted'
            : cameraStatus.state === 'denied'
              ? 'denied'
              : 'prompt'
        )

        setMicrophoneAccess(
          microphoneStatus.state === 'granted'
            ? 'granted'
            : microphoneStatus.state === 'denied'
              ? 'denied'
              : 'prompt'
        )

        updateShowPermissionsIfChanged(
          cameraStatus.state === 'granted',
          microphoneStatus.state === 'granted'
        )

        cameraStatus.onchange = () => {
          if (cameraAccessRef.current === 'inacessible') return
          const newState =
            cameraStatus.state === 'granted'
              ? 'granted'
              : cameraStatus.state === 'denied'
                ? 'denied'
                : 'prompt'
          setCameraAccess(newState)
          updateShowPermissionsIfChanged(
            cameraStatus.state === 'granted',
            microphoneStatus.state === 'granted'
          )
        }
        microphoneStatus.onchange = () => {
          const newState =
            microphoneStatus.state === 'granted'
              ? 'granted'
              : microphoneStatus.state === 'denied'
                ? 'denied'
                : 'prompt'
          setMicrophoneAccess(newState)
          updateShowPermissionsIfChanged(
            cameraStatus.state === 'granted',
            microphoneStatus.state === 'granted'
          )
        }
      } else {
        setCameraAccess('prompt')
        setMicrophoneAccess('prompt')
      }
    } catch (error: any) {
      console.error('Erro ao verificar permissões:', error)
      setCameraAccess('prompt')
      setMicrophoneAccess('prompt')
    }
  }

  const requestPermissions = async () => {
    setIsRequesting(true)
    try {
      const cameraResult = await checkSingleDevicePermission('camera')
      const microphoneResult = await checkSingleDevicePermission('microphone')

      setCameraAccess(cameraResult)
      setMicrophoneAccess(microphoneResult)
      updateShowPermissionsIfChanged(
        cameraResult === 'granted',
        microphoneResult === 'granted'
      )

      if (cameraResult === 'denied' || microphoneResult === 'denied') {
        setShowInstructions(true)
      } else {
        setShowInstructions(false)
      }
    } catch (error) {
      console.error('Erro ao verificar permissões:', error)
      const cameraResult = await checkSingleDevicePermission('camera')
      const microphoneResult = await checkSingleDevicePermission('microphone')
      setCameraAccess(cameraResult)
      setMicrophoneAccess(microphoneResult)
      updateShowPermissionsIfChanged(
        cameraResult === 'granted',
        microphoneResult === 'granted'
      )
    } finally {
      setIsRequesting(false)
    }
  }

  type NetworkQualityState = NonNullable<typeof networkQuality>
  const lastNetworkQualityRef = useRef<NetworkQualityState | null>(null)

  const setNetworkQualityIfChanged = useCallback((next: NetworkQualityState) => {
    const prev = lastNetworkQualityRef.current
    if (
      prev &&
      prev.effectiveType === next.effectiveType &&
      prev.downlink === next.downlink &&
      prev.rtt === next.rtt &&
      prev.status === next.status
    ) {
      return
    }
    lastNetworkQualityRef.current = next
    setNetworkQuality(next)
  }, [])

  const computeNetworkQuality = useCallback((): NetworkQualityState => {
    // @ts-ignore - navigator.connection pode não estar disponível em todos os navegadores
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (connection) {
      const effectiveType = connection.effectiveType || 'unknown'
      const downlink = connection.downlink || 0
      const rtt = connection.rtt || 0
      const saveData = connection.saveData || false
      let status: 'excellent' | 'good' | 'poor' | 'critical' = 'good'
      if (effectiveType === '4g' && downlink >= 10) status = 'excellent'
      else if (effectiveType === '4g' && downlink >= 1.5) status = 'good'
      else if (effectiveType === '3g' || (effectiveType === '4g' && downlink < 1.5)) status = 'poor'
      else if (effectiveType === '2g' || effectiveType === 'slow-2g') status = 'critical'
      else if (rtt > 1000) status = 'poor'
      else if (rtt > 2000) status = 'critical'
      return { effectiveType, downlink, rtt, saveData, status }
    }
    const isOnline = navigator.onLine
    return {
      effectiveType: isOnline ? 'unknown' : 'offline',
      downlink: 0,
      rtt: 0,
      saveData: false,
      status: isOnline ? 'good' : 'critical',
    }
  }, [])

  // Inicia a câmera quando a permissão for concedida
  const startCamera = async () => {
    if (cameraAccess !== 'granted') return

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch((error) => {
            console.error('Erro ao iniciar o vídeo:', error)
          })
        }
      }
    } catch (error: any) {
      setCameraAccess('inacessible')
   
      console.error('Erro ao acessar a câmera:', error)
    }
  }

  // Para a câmera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        if (track.readyState !== 'ended') {
          track.stop()
        }
      })
      streamRef.current = null
    }

    if (videoRef.current) {
      if (videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream
        stream.getTracks().forEach((track) => {
          if (track.readyState !== 'ended') {
            track.stop()
          }
        })
        videoRef.current.srcObject = null
      }
      videoRef.current.pause()
    }
  }

  // Tentar novamente quando a câmera está inacessível (ex.: outro app usando)
  const handleRetryCamera = async () => {
    stopCamera()
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      streamRef.current = stream
      setCameraAccess('granted')
      updateShowPermissionsIfChanged(true, showPermissions.microphone)
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch((err) => {
            console.error('Erro ao iniciar o vídeo:', err)
          })
        }
      }
    } catch (error: any) {
      if (
        error?.name === 'NotAllowedError' ||
        error?.name === 'PermissionDeniedError'
      ) {
        setCameraAccess('denied')
        setShowInstructions(true)
        updateShowPermissionsIfChanged(false, showPermissions.microphone)
      } else if (
        error?.name === 'NotReadableError' ||
        error?.name === 'NotFoundError'
      ) {
        setCameraAccess('inacessible')
      } else {
        setCameraAccess('inacessible')
      }
      console.error('Erro ao acessar a câmera:', error)
    }
  }

  useEffect(() => {
    checkPermissionStatus()

    setNetworkQualityIfChanged(computeNetworkQuality())
    // @ts-ignore
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (connection) {
      const onConnectionChange = () => {
        setNetworkQualityIfChanged(computeNetworkQuality())
      }
      connection.addEventListener('change', onConnectionChange)
      return () => connection.removeEventListener('change', onConnectionChange)
    }
  }, [computeNetworkQuality, setNetworkQualityIfChanged])

  // Inicia a câmera quando a permissão for concedida
  useEffect(() => {
    if (cameraAccess === 'granted') {
      startCamera()
    } else {
      stopCamera()
    }

    return () => {
      stopCamera()
    }
  }, [cameraAccess])

  const hasDeniedPermissions =
    ['denied', 'prompt', 'inacessible'].includes(cameraAccess ?? '') ||
    ['denied', 'prompt', 'inacessible'].includes(microphoneAccess ?? '')

  const getPermissionButtonText = () => {
    const cameraMissing = cameraAccess !== 'granted'
    const microphoneMissing = microphoneAccess !== 'granted'

    if (cameraMissing && microphoneMissing) {
      return 'Solicitar permissões'
    } else if (cameraMissing) {
      return 'Permitir acesso à câmera'
    } else if (microphoneMissing) {
      return 'Permitir acesso ao microfone'
    }
    return 'Solicitar permissões'
  }

  const modalTitleId = 'video-permissions-dialog-title'
  const modalDescId = 'video-permissions-dialog-desc'

  return (
    <>
      <S.Overlay
        onClick={onClose}
        data-testid="video-permissions-overlay"
        aria-hidden="true"
      />
      <S.ModalContainer
        onClick={(e) => e.stopPropagation()}
        data-testid="video-permissions-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        aria-describedby={modalDescId}
      >
        <S.ModalHeader data-testid="video-permissions-header">
          <S.ModalTitle id={modalTitleId} data-testid="video-permissions-title">
            Permissões Necessárias
          </S.ModalTitle>
          <S.CloseButton
            onClick={onClose}
            data-testid="video-permissions-close"
            type="button"
            aria-label="Fechar"
          >
            <CloseIcon size={24} color="#0C111D" aria-hidden />
          </S.CloseButton>
        </S.ModalHeader>
        <S.ModalContent id={modalDescId} data-testid="video-permissions-content">
          {showInstructions && hasDeniedPermissions && (
            <S.InstructionsBox
              data-testid="video-permissions-instructions"
              role="alert"
              aria-live="assertive"
            >
              <S.InstructionsIcon data-testid="video-permissions-instructions-icon" aria-hidden="true">
                ⚠
              </S.InstructionsIcon>
              <S.InstructionsContent data-testid="video-permissions-instructions-content">
                <S.InstructionsTitle data-testid="video-permissions-instructions-title">Permissões negadas</S.InstructionsTitle>
                <S.InstructionsText data-testid="video-permissions-instructions-text">
                  Por favor, permita o acesso à câmera e microfone nas configurações
                  do navegador.
                  {typeof window !== 'undefined' && 'chrome' in window && (
                    <span>
                      {' '}
                      Clique no ícone de cadeado na barra de endereço.
                    </span>
                  )}
                </S.InstructionsText>
              </S.InstructionsContent>
            </S.InstructionsBox>
          )}

          <S.PermissionsCard
            data-testid="video-permissions-card"
            role="region"
            aria-label="Status das Permissões"
          >
            <S.PermissionsTitle data-testid="video-permissions-card-title" id="video-permissions-status-heading">
              Status das Permissões
            </S.PermissionsTitle>
            <S.PermissionsList data-testid="video-permissions-list" role="list">
              <S.PermissionItem data-testid="video-permission-camera" role="listitem">
                <S.PermissionInfo>
                  <S.PermissionIcon status={cameraAccess} data-testid="video-permission-camera-icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </S.PermissionIcon>
                  <S.PermissionText data-testid="video-permission-camera-text">
                    <S.PermissionLabel data-testid="video-permission-camera-label">Câmera</S.PermissionLabel>
                    <S.StatusText status={cameraAccess} data-testid="video-permission-camera-status">
                      {CAMERA_ACCESS_STATES[cameraAccess as keyof typeof CAMERA_ACCESS_STATES]}
                    </S.StatusText>
                  </S.PermissionText>
                </S.PermissionInfo>
              </S.PermissionItem>

              <S.PermissionItem data-testid="video-permission-microphone" role="listitem">
                <S.PermissionInfo>
                  <S.PermissionIcon status={microphoneAccess} data-testid="video-permission-microphone-icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" />
                      <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  </S.PermissionIcon>
                  <S.PermissionText data-testid="video-permission-microphone-text">
                    <S.PermissionLabel data-testid="video-permission-microphone-label">Microfone</S.PermissionLabel>
                    <S.StatusText status={microphoneAccess} data-testid="video-permission-microphone-status">
                      {microphoneAccess === 'granted'
                        ? 'Permitido'
                        : microphoneAccess === 'prompt'
                          ? 'Aguardando permissão'
                          : 'Negado'}
                    </S.StatusText>
                  </S.PermissionText>
                </S.PermissionInfo>
              </S.PermissionItem>
            </S.PermissionsList>
            {cameraAccess === 'inacessible' && (
              <S.CameraNotAccessibleWarning
                data-testid="video-permissions-camera-inaccessible-warning"
                role="alert"
                aria-live="assertive"
              >
                <WarningAmberOutlined sx={{ fontSize: '16px' }} aria-hidden="true" />
                Feche outros apps que possam estar usando a câmera
              </S.CameraNotAccessibleWarning>
            )}
          </S.PermissionsCard>

          {networkQuality && (
            <S.CameraNetworkContainer
              data-testid="video-permissions-camera-network"
              role="region"
              aria-label="Preview da câmera e qualidade da conexão"
            >
              <S.CameraPreview data-testid="video-permissions-camera-preview">
                {cameraAccess === 'granted' ? (
                  <S.CameraVideo
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    data-testid="video-permissions-camera-video"
                    aria-label="Preview da câmera"
                  />
                ) : (
                  <S.CameraPlaceholder
                    data-testid="video-permissions-camera-placeholder"
                    role="img"
                    aria-label={
                      cameraAccess === 'denied'
                        ? 'Câmera bloqueada'
                        : cameraAccess === 'inacessible'
                          ? 'Câmera indisponível'
                          : 'Aguardando permissão'
                    }
                  >
                    <S.CameraPlaceholderIcon data-testid="video-permissions-camera-placeholder-icon" aria-hidden="true">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                    </S.CameraPlaceholderIcon>
                    <S.CameraPlaceholderText data-testid="video-permissions-camera-placeholder-text">
                      {cameraAccess === 'denied'
                        ? 'Câmera bloqueada'
                        : cameraAccess === 'inacessible'
                          ? 'Câmera indisponível'
                          : 'Aguardando permissão'}
                    </S.CameraPlaceholderText>
                  </S.CameraPlaceholder>
                )}
              </S.CameraPreview>
              <S.NetworkCard
                data-testid="video-permissions-network-card"
                role="status"
                aria-live="polite"
                aria-label="Qualidade da conexão de internet"
              >
                <S.NetworkTitle data-testid="video-permissions-network-title">
                  Qualidade da Conexão
                </S.NetworkTitle>
                <S.NetworkStatus data-testid="video-permissions-network-status">
                  <S.NetworkInfo data-testid="video-permissions-network-info">
                    <S.NetworkIcon status={networkQuality.status} data-testid="video-permissions-network-icon" aria-hidden="true">
                      <Wifi />
                    </S.NetworkIcon>
                    <S.NetworkText data-testid="video-permissions-network-text">
                      <S.NetworkLabel data-testid="video-permissions-network-label">Status da Internet</S.NetworkLabel>
                      <S.NetworkStatusText status={networkQuality.status} data-testid="video-permissions-network-status-text">
                        {networkQuality.status === 'excellent'
                          ? 'Excelente'
                          : networkQuality.status === 'good'
                            ? 'Boa'
                            : networkQuality.status === 'poor'
                              ? 'Ruim'
                              : networkQuality.status === 'critical'
                                ? 'Crítica'
                                : 'Offline'}
                      </S.NetworkStatusText>
                    </S.NetworkText>
                  </S.NetworkInfo>
                  {networkQuality.downlink > 0 && (
                    <S.NetworkDetails data-testid="video-permissions-network-details">
                      <S.NetworkDetailItem data-testid="video-permissions-network-downlink">
                        <span>Velocidade:</span>{' '}
                        <strong>{networkQuality.downlink.toFixed(1)} Mbps</strong>
                      </S.NetworkDetailItem>
                      {networkQuality.rtt != null && networkQuality.rtt > 0 && (
                        <S.NetworkDetailItem data-testid="video-permissions-network-rtt">
                          <span>Latência:</span> <strong>{networkQuality.rtt}ms</strong>
                        </S.NetworkDetailItem>
                      )}
                    </S.NetworkDetails>
                  )}
                </S.NetworkStatus>
              </S.NetworkCard>
            </S.CameraNetworkContainer>
          )}

          {networkQuality && (networkQuality.status === 'poor' || networkQuality.status === 'critical') && (
            <S.NetworkWarning
              data-testid="video-permissions-network-warning"
              role="alert"
              aria-live="assertive"
            >
              <S.WarningIcon data-testid="video-permissions-network-warning-icon" aria-hidden="true">
                ⚠️
              </S.WarningIcon>
              <S.WarningText data-testid="video-permissions-network-warning-text">
                <strong>Atenção:</strong> Sua conexão pode impactar negativamente a
                qualidade da videochamada. Recomendamos usar uma conexão mais estável
                ou reduzir o uso de outros aplicativos que consomem internet.
              </S.WarningText>
            </S.NetworkWarning>
          )} 

          <S.TipsSection
            hasDeniedPermissions={hasDeniedPermissions}
            data-testid="video-permissions-tips"
            role="list"
            aria-label="Dicas para a vídeochamada"
          >
            <S.TipItem data-testid="video-permissions-tip-light" role="listitem">
              <S.TipIcon data-testid="video-permissions-tip-light-icon" aria-hidden="true">

              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.2518 12.4903C16.9677 14.855 15.4187 15.7991 14.7496 16.4865C14.0806 17.1739 14.1997 17.3114 14.2455 18.3196C14.253 18.4445 14.2349 18.5696 14.1922 18.6872C14.1495 18.8049 14.0832 18.9125 13.9973 19.0035C13.9115 19.0945 13.8079 19.167 13.6929 19.2165C13.578 19.2659 13.4541 19.2913 13.329 19.2911H10.671C10.5459 19.2913 10.422 19.2659 10.3071 19.2165C10.1921 19.167 10.0885 19.0945 10.0027 19.0035C9.9168 18.9125 9.85048 18.8049 9.80779 18.6872C9.7651 18.5696 9.74695 18.4445 9.75446 18.3196C9.75446 17.3297 9.84612 17.1006 9.25036 16.4865C8.48963 15.7258 6.70236 14.6534 6.70236 11.7021C6.6977 10.9691 6.84495 10.243 7.13485 9.5697C7.42475 8.89639 7.851 8.29045 8.38672 7.79008C8.92245 7.2897 9.55604 6.90574 10.2475 6.66241C10.939 6.41908 11.6734 6.32166 12.4045 6.37628C13.1355 6.4309 13.8473 6.63638 14.4949 6.97977C15.1426 7.32316 15.7121 7.79701 16.1675 8.37144C16.6229 8.94587 16.9544 9.60843 17.141 10.3173C17.3276 11.0262 17.3654 11.7661 17.2518 12.4903Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M10.4602 19.2361V20.7484C10.4602 21.1609 10.6893 21.5 10.9735 21.5H13.0265C13.3107 21.5 13.5398 21.1609 13.5398 20.7484V19.2361" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M11.2209 8.69585C10.6302 8.69585 10.0637 8.9305 9.64606 9.34818C9.22838 9.76586 8.99373 10.3324 8.99373 10.9231" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M19.3324 11.9037H21.1655" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M17.4859 5.89209L18.7874 4.59059" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M18.4859 16.9992L19.7874 18.3007" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M11.9995 2.37708L12 3.85899" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M5.23993 4.59059L6.53226 5.89209" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4.23993 18.3007L5.53226 16.9992" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4.66763 11.9037H2.83453" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>

              </S.TipIcon>
              <S.TipText data-testid="video-permissions-tip-light-text">
                <strong>Luz importa:</strong> Escolha um local bem iluminado,
                preferencialmente com luz natural.
              </S.TipText>
            </S.TipItem>
            <S.TipItem data-testid="video-permissions-tip-framing" role="listitem">
              <S.TipIcon data-testid="video-permissions-tip-framing-icon" aria-hidden="true">
                <FaceRecognitionIcon size={16}  aria-hidden />
              </S.TipIcon>
              <S.TipText data-testid="video-permissions-tip-framing-text">
                <strong>Enquadramento:</strong> Certifique-se de que seu rosto esteja
                centralizado na tela.
              </S.TipText>
            </S.TipItem>
          </S.TipsSection>

          <S.ButtonsContainer data-testid="video-permissions-buttons">
            {hasDeniedPermissions && cameraAccess !== 'inacessible' && (
              <Button
                color="primary"
                type="button"
                size="normal"
                styles={{ width: '100%' }}
                onClick={requestPermissions}
                disabled={isRequesting}
                data-testid="video-permissions-request-button"
              >
                {getPermissionButtonText() + (isRequesting ? '...' : '')}
              </Button>
            )}
            {cameraAccess === 'inacessible' && (
              <Button
                color="orange-outline"
                type="button"
                size="normal"
                styles={{ width: '100%' }}
                onClick={handleRetryCamera}
                data-testid="video-permissions-retry-button"
              >
                Tentar novamente
              </Button>
            )}
            <Button
              color="success"
              type="button"
              size="normal"
              styles={{ width: '100%' }}
              onClick={onConfirm}
              disabled={hasDeniedPermissions}
              data-testid="video-permissions-confirm-button"
            >
              Entrar na vídeochamada
            </Button>
          </S.ButtonsContainer>
        </S.ModalContent>
      </S.ModalContainer>
    </>
  )
}

const VideoPermissions = React.memo(VideoPermissionsInner)

export default VideoPermissions
